import type { MspCustomer } from '@/types/mspCustomer';

/**
 * Ordering and aggregation for the MSP dashboard.
 *
 * Pure and separate from the page so the rules that decide *who is worst* can be
 * tested directly, rather than inferred from rendered DOM.
 */

/** Worst first. An unrecognised status sorts last rather than jumping the queue. */
export const HEALTH_RANK: Record<string, number> = { critical: 0, warning: 1, healthy: 2 };
/**
 * Incident severity, worst first — a different vocabulary from HEALTH_RANK above,
 * which is division *health* and keeps its own `warning`. Incident severity has no
 * `warning`: it used to be ranked here *above* `medium`, so every row that carried
 * one sorted as more urgent than a genuine `medium`.
 */
export const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

const UNRANKED = 99;

export function healthOf(customer: MspCustomer): string {
  return customer.rollup?.healthStatus ?? 'healthy';
}

function healthRank(customer: MspCustomer): number {
  return HEALTH_RANK[healthOf(customer)] ?? UNRANKED;
}

function severityRank(severity: string | null | undefined): number {
  if (!severity) return UNRANKED;
  return SEVERITY_RANK[severity] ?? UNRANKED;
}

/**
 * Worst-first triage order.
 *
 * Health, then the *severity* of the worst open incident, then how many there
 * are, then how much of the fleet is unreachable, then name, then id.
 *
 * Severity outranks count deliberately: one critical incident is a worse morning
 * than three low ones, and ranking by count alone would bury it. The id at the
 * end makes the order total, so two customers sharing a name — or a refetch
 * returning rows in a different order — can't make the board shuffle.
 */
export function compareCustomersByUrgency(a: MspCustomer, b: MspCustomer): number {
  const health = healthRank(a) - healthRank(b);
  if (health !== 0) return health;

  const severity = severityRank(a.rollup?.worstOpenSeverity) - severityRank(b.rollup?.worstOpenSeverity);
  if (severity !== 0) return severity;

  const incidents = (b.rollup?.openIncidentCount ?? 0) - (a.rollup?.openIncidentCount ?? 0);
  if (incidents !== 0) return incidents;

  const unreachable = (b.rollup?.deviceUnreachable ?? 0) - (a.rollup?.deviceUnreachable ?? 0);
  if (unreachable !== 0) return unreachable;

  const name = a.name.localeCompare(b.name);
  if (name !== 0) return name;

  return a.divisionId.localeCompare(b.divisionId);
}

/**
 * Which customers the dashboard's numbers cover (frontend#13).
 *
 * An offboarded customer is never counted: one left with open incidents is
 * almost always leftovers, and its noise drowned the live book. It still counts
 * toward the plan (billing never reads lifecycle) and stays listed under All
 * customers, where the lifecycle filter shows it. A prospect counts while
 * `includeProspects` is on: its trial gateway's trouble is real work, and it
 * matters most during the sale.
 */
export const OFFBOARDED = 'offboarded';
export const PROSPECT = 'prospect';

export function countsOnDashboard(customer: MspCustomer, includeProspects: boolean): boolean {
  if (customer.lifecycle === OFFBOARDED) return false;
  if (customer.lifecycle === PROSPECT) return includeProspects;
  return true;
}

export interface PortfolioTotals {
  /** Signed customers: neither prospects nor offboarded. */
  customers: number;
  /** Prospects counted alongside them (none while prospects are left out). */
  prospects: number;
  needAttention: number;
  critical: number;
  devices: number;
  unreachable: number;
  gateways: number;
  gatewaysOnline: number;
  openIncidents: number;
  criticalIncidents: number;
  bySeverity: Record<string, number>;
}

/**
 * Book-wide totals over the customers the dashboard counts
 * ([`countsOnDashboard`]). Each counted customer contributes, whether or not it
 * has a rollup.
 */
export function summarizePortfolio(
  customers: readonly MspCustomer[],
  { includeProspects = true }: { includeProspects?: boolean } = {},
): PortfolioTotals {
  const bySeverity: Record<string, number> = {};
  // Tracked alongside the map: an index into Record<string, number> looks
  // non-nullable to the compiler even though a missing severity really is absent.
  let criticalIncidents = 0;
  let devices = 0;
  let unreachable = 0;
  let gateways = 0;
  let gatewaysOnline = 0;
  let openIncidents = 0;
  let needAttention = 0;
  let critical = 0;
  let signed = 0;
  let prospects = 0;

  for (const customer of customers) {
    if (!countsOnDashboard(customer, includeProspects)) continue;
    if (customer.lifecycle === PROSPECT) prospects += 1;
    else signed += 1;
    const rollup = customer.rollup;
    devices += rollup?.deviceTotal ?? 0;
    unreachable += rollup?.deviceUnreachable ?? 0;
    gateways += rollup?.gatewayCount ?? 0;
    gatewaysOnline += rollup?.gatewaysOnline ?? 0;
    openIncidents += rollup?.openIncidentCount ?? 0;

    const status = healthOf(customer);
    if (status !== 'healthy') needAttention += 1;
    if (status === 'critical') critical += 1;

    for (const [severity, count] of Object.entries(rollup?.openIncidentsBySeverity ?? {})) {
      bySeverity[severity] = (bySeverity[severity] ?? 0) + count;
      if (severity === 'critical') criticalIncidents += count;
    }
  }

  return {
    customers: signed,
    prospects,
    needAttention,
    critical,
    devices,
    unreachable,
    gateways,
    gatewaysOnline,
    openIncidents,
    criticalIncidents,
    bySeverity,
  };
}
